import { SolType } from './sol-type';
import { SolViewOptions } from './sol-view-options';
import { SolViewStringOptions } from './sol-view-string-options';
import { SolViewSelectOptions } from './sol-view-select-options';
import { SolViewCheckboxOptions } from './sol-view-checkbox-options';
import { SolViewDateOptions } from './sol-view-date-options';
import { SolViewRelativeDateOptions } from './sol-view-relative-date-options';
import { SolViewSeverityOptions } from './sol-view-severity-options';
import { SolLayout } from './sol-layout';
import { SolViewTextOptions } from './sol-view-text-options';
import { SolViewButtonGroupOptions } from './sol-view-button-group-options';
import { SolViewQueryOptions } from './sol-view-query-options';
import { SolViewTagColorOptions } from './sol-view-tag-color-options';
import { SolViewTagOptions } from './sol-view-tag-options';
import { SolViewImageOptions } from './sol-view-image-options';

/**
 * The core view id for a sol data type (SolType). This id is used to allow data types to be
 * visualized (read and write) in various ways.
 *
 * For example SolType.string can be visualized by:
 * - SolViewId.text (text field)
 * - SolViewId.select (dropdown)
 * - SolViewId.radiobuttons (radiobutton group)
 * - SolViewId.bigRadiobuttons (big radiobutton group)
 *
 * @see Sol
 * @see SolForm
 * @see SolQuery
 * @see SolTableDef
 * @see SolViewComponent
 * @see ViewService
 */
export class SolViewId<O> {
  public static readonly any = SolViewId.of<SolViewOptions>('any');
  public static readonly text = SolViewId.of<SolViewTextOptions>('text', {
    truncated: false,
    color: 'ink',
    layout: SolLayout.of().withGap('0').withPy('4'),
  });
  public static readonly textField = SolViewId.of<SolViewStringOptions>('text', {
    truncated: false,
    color: 'ink',
    layout: SolLayout.of().withGap('0').withPy('4'),
  });
  public static readonly textArea = SolViewId.of<SolViewStringOptions>('textArea', {
    truncated: false,
    color: 'ink',
  });
  public static readonly markdown = SolViewId.of<SolViewStringOptions>('markdown', {
    truncated: false,
    color: 'ink',
  });
  public static readonly select = SolViewId.of<SolViewSelectOptions>('select');
  public static readonly booleanSelect = SolViewId.of<SolViewSelectOptions>('booleanSelect');
  public static readonly multiSelect = SolViewId.of<SolViewSelectOptions>('multiSelect');
  public static readonly checkbox = SolViewId.of<SolViewCheckboxOptions>('checkbox', {
    layout: SolLayout.of().withGap('8').withPy('8'),
  });
  public static readonly switch = SolViewId.of<SolViewCheckboxOptions>('switch');
  public static readonly radiobutton = SolViewId.of<SolViewSelectOptions>('radiobutton', {
    layout: SolLayout.of().withGap('8').withPy('8'),
  });
  public static readonly buttonGroup = SolViewId.of<SolViewButtonGroupOptions>('buttonGroup', {
    layout: SolLayout.of().withType('horizontal').withGap('4').withP('4'),
  });
  public static readonly bigCheckbox = SolViewId.of<SolViewSelectOptions>('bigCheckbox', {
    layout: SolLayout.of().withType('grid').withGrid('50 50').withGap('8'),
  });
  public static readonly bigRadiobutton = SolViewId.of<SolViewSelectOptions>('bigRadiobutton', {
    layout: SolLayout.of().withType('grid').withGrid('50 50').withGap('8'),
  });
  public static readonly date = SolViewId.of<SolViewDateOptions>('date', {
    format: 'yyyy-MM-dd HH:mm',
    color: 'ink',
    layout: SolLayout.of().withGap('0').withPy('4'),
  });
  public static readonly icon = SolViewId.of<SolViewOptions>('icon');
  public static readonly tag = SolViewId.of<SolViewTagOptions>('tag', { truncated: false });
  public static readonly number = SolViewId.of<SolViewStringOptions>('number', {
    truncated: false,
    color: 'ink',
    layout: SolLayout.of().withGap('0').withPy('4'),
  });
  public static readonly severity = SolViewId.of<SolViewSeverityOptions>('severity', {
    maxValue: 100,
    format: 'percentage',
    decimalCount: 0,
  });
  public static readonly trend = SolViewId.of<SolViewOptions>('trend');
  public static readonly button = SolViewId.of<SolViewOptions>('button', {
    layout: SolLayout.of().withGap('8').withAlignX('left'),
  });
  public static readonly log = SolViewId.of<SolViewOptions>('log');
  public static readonly budget = SolViewId.of<SolViewOptions>('budget');
  public static readonly fileUpload = SolViewId.of<SolViewOptions>('fileUpload');
  public static readonly image = SolViewId.of<SolViewImageOptions>('image');
  public static readonly diff = SolViewId.of<SolViewOptions>('diff');
  public static readonly queryField = SolViewId.of<SolViewQueryOptions>('queryField');
  public static readonly tagColor = SolViewId.of<SolViewTagColorOptions>('tagColor', { allowCustom: true });
  public static readonly relativeDate = SolViewId.of<SolViewRelativeDateOptions>('relativeDate', {
    color: 'ink',
    layout: SolLayout.of().withGap('0').withPy('4'),
  });

  private constructor(
    public readonly id: string,
    public readonly defaultOptions: Partial<O>,
  ) {}

  public static of<O>(id: string, defaultOptions: Partial<O> = {}): SolViewId<O> {
    return new SolViewId<O>(id, defaultOptions);
  }

  public toOptions<T>(dataType: SolType<T>, options: Partial<O> = {}): O {
    return {
      ...this.defaultOptions,
      dataType,
      disabled: false,
      ...options,
    } as O;
  }
}
